import { Command } from 'cmdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { RouteListItem } from '@xingroute/schema'
import { api } from '../api/client.js'
import { COMMAND_PALETTE_OPEN_EVENT, useShortcuts } from '../hooks/useShortcuts.js'
import { friendlyError } from '../lib/friendlyError.js'
import { useLocale } from '../locale/LocaleContext.js'

interface PaletteItem {
  id: string
  label: string
  subtitle: string
  to: string
  keywords: string[]
}

const PAGE_DEFS = [
  {
    id: 'page-hiking',
    labelKey: 'navHiking',
    subtitle: '/discover',
    to: '/discover',
    keywords: ['hiking', '徒步', '登山', '路线', 'routes'],
  },
  {
    id: 'page-discover',
    labelKey: 'navDiscover',
    subtitle: '/discover',
    to: '/discover',
    keywords: ['routes', '线路', 'browse', 'explore'],
  },
  {
    id: 'page-me',
    labelKey: 'navMe',
    subtitle: '/me',
    to: '/me',
    keywords: ['profile', 'account', '个人中心'],
  },
  {
    id: 'page-settings',
    labelKey: 'settings',
    subtitle: '/me/settings',
    to: '/me/settings',
    keywords: ['preferences', '账户', '配置'],
  },
] as const

export function CommandPalette() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState(false)
  const [routes, setRoutes] = useState<RouteListItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pageItems = useMemo<PaletteItem[]>(
    () =>
      PAGE_DEFS.map((item) => ({
        id: item.id,
        label: t(item.labelKey),
        subtitle: item.subtitle,
        to: item.to,
        keywords: [...item.keywords],
      })),
    [t],
  )

  useShortcuts({ onToggleCommandPalette: () => setOpen((v) => !v) })

  useEffect(() => {
    function onOpen() {
      setOpen(true)
    }
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen)
    return () => window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open || routes !== null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .listRoutes()
      .then((items) => {
        if (!cancelled) setRoutes(items)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(friendlyError(err, t))
          setRoutes(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, routes, t])

  const routeItems = useMemo<PaletteItem[]>(() => {
    return (routes ?? []).map((route) => ({
      id: `route-${route.id}`,
      label: route.title,
      subtitle: route.summary || route.slug,
      to: route.slug ? `/r/${route.slug}` : `/routes/${route.id}`,
      keywords: [
        route.slug,
        route.status,
        route.summary ?? '',
        route.activities.join(' '),
        route.distance_km != null ? `${route.distance_km.toFixed(1)} km` : '',
      ].filter(Boolean),
    }))
  }, [routes])

  function selectItem(to: string) {
    setOpen(false)
    navigate(to)
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-[var(--xr-overlay-bg)] px-3 pt-[12vh]"
      data-command-palette
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setOpen(false)
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('aria.commandPalette')}
        className="w-full max-w-2xl overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] shadow-[var(--xr-shadow-md)]"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            setOpen(false)
          }
        }}
      >
        <Command filter={fuzzyFilter} loop>
          <div className="border-b border-[var(--xr-border-default)] p-3">
            <Command.Input
              ref={inputRef}
              placeholder={t('commandPalette.placeholder')}
              className="w-full bg-transparent text-base text-[var(--xr-text-primary)] outline-none placeholder:text-[var(--xr-text-muted)]"
            />
          </div>
          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="px-3 py-8 text-center text-sm text-[var(--xr-text-muted)]">
              {t('commandPalette.empty')}
            </Command.Empty>

            <Command.Group
              heading={t('commandPalette.pages')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--xr-text-muted)]"
            >
              {pageItems.map((item) => (
                <CommandRow key={item.id} item={item} onSelect={selectItem} />
              ))}
            </Command.Group>

            <Command.Group
              heading={t('commandPalette.routes')}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[var(--xr-text-muted)]"
            >
              {loading && (
                <div className="px-3 py-2 text-sm text-[var(--xr-text-muted)]">
                  {t('commandPalette.loadingRoutes')}
                </div>
              )}
              {error && <div className="px-3 py-2 text-sm text-[var(--xr-danger)]">{error}</div>}
              {routeItems.map((item) => (
                <CommandRow key={item.id} item={item} onSelect={selectItem} />
              ))}
            </Command.Group>
          </Command.List>
          <div className="flex items-center justify-between border-t border-[var(--xr-border-default)] px-3 py-2 text-xs text-[var(--xr-text-muted)]">
            <span>{t('commandPalette.openHint')}</span>
            <span>{t('commandPalette.toggleHint')}</span>
          </div>
        </Command>
      </div>
    </div>
  )
}

function CommandRow({ item, onSelect }: { item: PaletteItem; onSelect: (to: string) => void }) {
  return (
    <Command.Item
      value={`${item.label} ${item.subtitle}`}
      keywords={item.keywords}
      onSelect={() => onSelect(item.to)}
      className="flex cursor-pointer items-center justify-between gap-4 rounded-[var(--xr-radius-md)] px-3 py-2 text-sm text-[var(--xr-text-primary)] outline-none aria-selected:bg-[var(--xr-bg-surface-elev)]"
    >
      <span className="min-w-0">
        <span className="block truncate font-medium">{item.label}</span>
        <span className="block truncate text-xs text-[var(--xr-text-muted)]">{item.subtitle}</span>
      </span>
      <span className="flex-none text-xs text-[var(--xr-text-muted)]">{item.to}</span>
    </Command.Item>
  )
}

function fuzzyFilter(value: string, search: string, keywords?: string[]): number {
  const query = search.trim().toLowerCase()
  if (!query) return 1
  const haystack = `${value} ${(keywords ?? []).join(' ')}`.toLowerCase()
  if (haystack.includes(query)) return 1

  let score = 0
  let lastIndex = -1
  for (const char of query) {
    const nextIndex = haystack.indexOf(char, lastIndex + 1)
    if (nextIndex === -1) return 0
    score += nextIndex === lastIndex + 1 ? 2 : 1
    lastIndex = nextIndex
  }
  return score / (query.length * 2 + Math.max(1, lastIndex))
}
